import React, { useRef } from 'react';
import { CheckCircleIcon } from './icons/InterfaceIcons';
import { generateCertificatePDF } from '../utils/pdfGenerator';

interface TestCertificateProps {
    user: {
        name: string;
        email: string;
    };
    test: {
        name: string;
        duration: number;
        totalMarks: number;
    };
    results: {
        score: number;
        accuracy: number;
        correct: number;
        incorrect: number;
        unattempted: number;
        time_taken: number;
    };
    onClose: () => void;
}

export const TestCertificate: React.FC<TestCertificateProps> = ({ user, test, results, onClose }) => {
    const certificateRef = useRef<HTMLDivElement>(null);
    const completionDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    });

    const handleDownload = async () => {
        try {
            await generateCertificatePDF(user, test, results);
        } catch (error) {
            console.error('Error downloading certificate:', error);
        }
    };

    const getPerformanceLevel = () => {
        if (results.accuracy >= 90) return { level: 'Excellent', color: 'text-green-600', bgColor: 'bg-green-100' };
        if (results.accuracy >= 75) return { level: 'Good', color: 'text-blue-600', bgColor: 'bg-blue-100' };
        if (results.accuracy >= 60) return { level: 'Satisfactory', color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
        return { level: 'Needs Improvement', color: 'text-red-600', bgColor: 'bg-red-100' };
    };

    const performance = getPerformanceLevel();

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
                <div ref={certificateRef} className="p-8">
                    {/* Certificate Header */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                            <CheckCircleIcon className="w-10 h-10 text-green-600" />
                        </div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">Certificate of Completion</h1>
                        <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto"></div>
                    </div>

                    {/* Certificate Body */}
                    <div className="text-center mb-8">
                        <p className="text-lg text-gray-600 mb-4">This is to certify that</p>
                        <h2 className="text-4xl font-bold text-gray-800 mb-4">{user.name}</h2>
                        <p className="text-lg text-gray-600 mb-2">has successfully completed the test</p>
                        <h3 className="text-2xl font-semibold text-blue-600 mb-6">"{test.name}"</h3>
                        
                        <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${performance.bgColor} ${performance.color} mb-6`}>
                            Performance: {performance.level}
                        </div>
                    </div>

                    {/* Test Results */}
                    <div className="bg-gray-50 rounded-lg p-6 mb-8">
                        <h4 className="text-lg font-semibold text-gray-800 mb-4 text-center">Test Results</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                            <div>
                                <div className="text-2xl font-bold text-blue-600">{results.score}</div>
                                <div className="text-sm text-gray-600">Score</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-green-600">{results.accuracy}%</div>
                                <div className="text-sm text-gray-600">Accuracy</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-purple-600">{results.correct + results.incorrect}</div>
                                <div className="text-sm text-gray-600">Attempted</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-orange-600">{Math.floor(results.time_taken/60)}m</div>
                                <div className="text-sm text-gray-600">Time Taken</div>
                            </div>
                        </div>
                    </div>

                    {/* Certificate Footer */}
                    <div className="text-center">
                        <p className="text-sm text-gray-600 mb-2">Date of Completion</p>
                        <p className="text-lg font-semibold text-gray-800 mb-6">{completionDate}</p>
                        
                        <div className="border-t border-gray-300 pt-4">
                            <p className="text-sm text-gray-500">TestSeries - Professional Exam Practice Portal</p>
                            <p className="text-xs text-gray-400">Empowering students through quality practice tests</p>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="border-t border-gray-200 p-6 bg-gray-50 rounded-b-xl">
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <button 
                            onClick={handleDownload}
                            className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                        >
                            Download Certificate
                        </button>
                        <button 
                            onClick={onClose}
                            className="px-6 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};